import { useEffect, useMemo, useState } from "react";
import { Building2, Plus, UserPlus, Loader2, ExternalLink, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Platform-owner card on /accounts for managing dealer groups.
 *
 * What it does:
 *   1. Create a group (e.g. "RRG Group")
 *   2. Assign workshops to a group (or move between groups)
 *   3. Promote / demote a user to Group Aftersales Manager
 *
 * Read-only group dashboard for the elevated user lives at /group.
 */
export default function GroupsAdminCard() {
  const [groups, setGroups] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  const load = async () => {
    try {
      const [g, u] = await Promise.all([
        api.get("/admin/groups"),
        api.get("/admin/users"),
      ]);
      setGroups(g.data?.groups || []);
      const allUsers = u.data?.users || [];
      setUsers(allUsers);
      // Distinct workshops gleaned from the users payload
      const seen = new Map();
      for (const usr of allUsers) {
        if (usr.workshop_id && !seen.has(usr.workshop_id)) {
          seen.set(usr.workshop_id, {
            id: usr.workshop_id,
            name: usr.workshop_name || usr.workshop_id.slice(0, 8),
            group_id: usr.workshop_group_id || null,
          });
        }
      }
      // Augment with workshop.group_id by re-fetching billing/commissions (which has it)
      try {
        const c = await api.get("/billing/commissions");
        for (const row of c.data?.by_workshop || []) {
          if (seen.has(row.workshop_id)) {
            seen.get(row.workshop_id).group_id = row.group_id || seen.get(row.workshop_id).group_id;
          }
        }
      } catch { /* non-fatal */ }
      setWorkshops([...seen.values()].sort((a, b) => a.name.localeCompare(b.name)));
    } catch { toast.error("Couldn't load groups"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post("/admin/groups", { name });
      toast.success(`Group "${name}" created`);
      setNewGroupName("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create group");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <section className="mt-8 rounded-xl border border-border bg-card p-6">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto" />
      </section>
    );
  }

  return (
    <section className="mt-8 rounded-xl border border-border bg-card p-6" data-testid="groups-admin-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 grid place-items-center shrink-0">
          <Building2 className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-2xl tracking-tight">Dealer Groups</h2>
          <p className="text-sm text-white/60 mt-1">
            Group multiple dealerships under a single parent entity (e.g. <em>Toyota Group North</em>) and promote a user to Group Aftersales Manager — they'll see a read-only rollup of every site at <code className="text-xs px-1.5 py-0.5 bg-muted rounded">/group</code>.
          </p>
        </div>
      </div>

      {/* Create new group */}
      <div className="mt-6 rounded-lg border border-border bg-muted/30 p-4 flex flex-col sm:flex-row gap-3" data-testid="groups-create-row">
        <input
          type="text"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && createGroup()}
          placeholder="e.g. Toyota Group, Marshall Motors, Lookers UK…"
          className="input-base flex-1"
          data-testid="new-group-name-input"
        />
        <button
          onClick={createGroup}
          disabled={creating || !newGroupName.trim()}
          className="btn-primary"
          data-testid="create-group-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create group
        </button>
      </div>

      {/* Existing groups */}
      {groups.length === 0 ? (
        <div className="mt-6 text-center text-sm text-white/50 py-8" data-testid="groups-empty">
          No groups yet — create one above to get started.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {groups.map((g) => (
            <GroupRow
              key={g.id}
              group={g}
              workshops={workshops}
              users={users}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </section>
  );
}


function GroupRow({ group, workshops, users, onChanged }) {
  const [open, setOpen] = useState(false);
  const inGroup = useMemo(
    () => workshops.filter((w) => w.group_id === group.id),
    [workshops, group.id],
  );
  const available = useMemo(
    () => workshops.filter((w) => w.group_id !== group.id),
    [workshops, group.id],
  );
  const managers = useMemo(
    () => users.filter((u) => u.is_group_manager && u.group_id === group.id),
    [users, group.id],
  );

  const assign = async (workshopId, newGroupId) => {
    try {
      await api.patch(`/admin/workshops/${workshopId}/group`, { group_id: newGroupId });
      toast.success(newGroupId ? "Added to group" : "Removed from group");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't assign workshop");
    }
  };

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden" data-testid={`group-row-${group.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/30"
        data-testid={`group-row-toggle-${group.id}`}
      >
        <div className="flex items-center gap-3 text-left">
          <Building2 className="w-4 h-4 text-primary" />
          <div>
            <div className="font-semibold">{group.name}</div>
            <div className="text-xs text-white/50">
              {inGroup.length} dealer{inGroup.length === 1 ? "" : "s"} · {managers.length} manager{managers.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="p-4 border-t border-border space-y-5">
          {/* Dealers in this group */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">In group</div>
            {inGroup.length === 0 ? (
              <div className="text-sm text-white/50 italic">No dealerships yet — add some below.</div>
            ) : (
              <ul className="space-y-1.5">
                {inGroup.map((w) => (
                  <li key={w.id} className="flex items-center justify-between rounded bg-muted/30 px-3 py-2" data-testid={`group-dealer-${w.id}`}>
                    <span className="text-sm font-semibold">{w.name}</span>
                    <button
                      onClick={() => assign(w.id, null)}
                      className="text-xs text-white/60 hover:text-destructive"
                      title="Remove from group"
                      data-testid={`group-dealer-remove-${w.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Add a dealer */}
          {available.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">Add a dealer</div>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    assign(e.target.value, group.id);
                    e.target.value = "";
                  }
                }}
                className="input-base text-sm"
                data-testid={`group-add-dealer-${group.id}`}
                defaultValue=""
              >
                <option value="">Select a workshop…</option>
                {available.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} {w.group_id ? "(currently in another group)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Promote a manager */}
          <PromoteManagerForm group={group} users={users} managers={managers} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}


function PromoteManagerForm({ group, users, managers, onChanged }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const promote = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/groups/promote-manager", {
        email: email.trim().toLowerCase(),
        group_id: group.id,
        promote: true,
      });
      toast.success("User promoted to Group Aftersales Manager");
      setEmail("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't promote user");
    } finally {
      setBusy(false);
    }
  };

  const demote = async (mgr) => {
    if (!window.confirm(`Demote ${mgr.email}? They'll lose group-level access.`)) return;
    try {
      await api.post("/admin/groups/promote-manager", {
        email: mgr.email,
        group_id: group.id,
        promote: false,
      });
      toast.success("User demoted");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't demote");
    }
  };

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">Group managers (read-only)</div>
      {managers.length > 0 && (
        <ul className="space-y-1.5 mb-3">
          {managers.map((m) => (
            <li key={m.id} className="flex items-center justify-between rounded bg-muted/30 px-3 py-2 text-sm" data-testid={`group-manager-${m.id}`}>
              <div>
                <div className="font-semibold">{m.name || m.email}</div>
                <div className="text-[11px] text-white/50 font-mono">{m.email}</div>
              </div>
              <button onClick={() => demote(m)} className="text-xs text-white/60 hover:text-destructive">
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && promote()}
          placeholder="email of existing user to promote"
          className="input-base flex-1 text-sm"
          data-testid={`group-promote-email-${group.id}`}
        />
        <button onClick={promote} disabled={busy || !email.trim()} className="btn-outline text-sm" data-testid={`group-promote-btn-${group.id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
          Promote
        </button>
      </div>
      <p className="text-[11px] text-white/50 mt-2">
        Promoted users see a read-only rollup of every dealership in this group at <code>/group</code>. They cannot edit jobs, send quotes or take payments.
      </p>
    </div>
  );
}
